import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaEye,
  FaLink,
  FaPrint,
  FaShareAlt,
} from "react-icons/fa";
import {
  LuArrowLeft,
  LuArrowRight,
  LuCheck,
  LuChevronDown,
  LuChevronRight,
  LuFileText,
  LuInfo,
  LuMail,
  LuMessageCircle,
  LuSearch,
  LuThumbsDown,
  LuThumbsUp,
  LuTicket,
} from "react-icons/lu";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import {
  HELP_ARTICLE_BY_ID,
  HELP_ARTICLES,
  HELP_CATEGORY_LABELS,
  getArticleNeighbors,
  getArticleTopicPath,
  getRelatedArticles,
} from "../../data/helpArticleCatalog";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/help_article_detail.scss";

function Callout({ callout }) {
  if (!callout) return null;
  return (
    <aside className={`help-doc-callout help-doc-callout--${callout.tone || "info"}`}>
      <LuInfo aria-hidden />
      <div>
        <strong>{callout.title}</strong>
        <p>{callout.text}</p>
      </div>
    </aside>
  );
}

function HowToContent({ article }) {
  return (
    <>
      <section id="overview" className="help-doc-section">
        <h2>Overview</h2>
        <p>{article.overview}</p>
      </section>
      {article.prerequisites?.length ? (
        <section id="before-you-start" className="help-doc-section">
          <h2>Before you start</h2>
          <ul className="help-doc-checklist">
            {article.prerequisites.map((item) => (
              <li key={item}>
                <LuCheck aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section id="steps" className="help-doc-section">
        <h2>Steps</h2>
        <ol className="help-doc-steps">
          {article.steps.map(([title, text], index) => (
            <li key={title}>
              <span>{index + 1}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
      <Callout callout={article.callout} />
    </>
  );
}

function TroubleshootingContent({ article }) {
  return (
    <>
      <section id="problem" className="help-doc-section">
        <h2>Problem</h2>
        <p>{article.overview}</p>
        <div className="help-doc-symptoms">
          {article.symptoms.map((symptom) => (
            <div key={symptom}>{symptom}</div>
          ))}
        </div>
      </section>
      <section id="possible-causes" className="help-doc-section">
        <h2>Possible causes</h2>
        <ul className="help-doc-bullets">
          {article.causes.map((cause) => (
            <li key={cause}>{cause}</li>
          ))}
        </ul>
      </section>
      <section id="solutions" className="help-doc-section">
        <h2>Solutions</h2>
        <div className="help-doc-solutions">
          {article.solutions.map(([title, text], index) => (
            <article key={title}>
              <span>{index + 1}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
      <Callout callout={article.callout} />
    </>
  );
}

function FaqContent({ article }) {
  const [openQuestions, setOpenQuestions] = useState(() => new Set([0]));

  return (
    <>
      <section id="overview" className="help-doc-section">
        <h2>Overview</h2>
        <p>{article.overview}</p>
      </section>
      <section id="questions" className="help-doc-section">
        <h2>Common questions</h2>
        <div className="help-doc-faq">
          {article.questions.map(([question, answer], index) => {
            const open = openQuestions.has(index);
            return (
              <article key={question} className={open ? "is-open" : ""}>
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() =>
                    setOpenQuestions((current) => {
                      const next = new Set(current);
                      if (next.has(index)) next.delete(index);
                      else next.add(index);
                      return next;
                    })
                  }
                >
                  <span>{question}</span>
                  <LuChevronDown aria-hidden />
                </button>
                {open ? <p>{answer}</p> : null}
              </article>
            );
          })}
        </div>
      </section>
      <Callout callout={article.callout} />
    </>
  );
}

function PolicyContent({ article }) {
  return (
    <>
      <section id="overview" className="help-doc-section">
        <h2>Policy overview</h2>
        <p>{article.overview}</p>
      </section>
      <section id="requirements" className="help-doc-section">
        <h2>Key requirements</h2>
        <div className="help-doc-rules">
          {article.rules.map(([title, text], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
      {article.sections?.map(([title, text], index) => (
        <section id={`guidance-${index + 1}`} className="help-doc-section" key={title}>
          <h2>{title}</h2>
          <p>{text}</p>
        </section>
      ))}
      <Callout callout={article.callout} />
    </>
  );
}

function OverviewContent({ article }) {
  return (
    <>
      <section id="overview" className="help-doc-section">
        <h2>Overview</h2>
        <p>{article.overview}</p>
      </section>
      <section id="benefits" className="help-doc-section">
        <h2>Key benefits</h2>
        <div className="help-doc-benefits">
          {article.benefits.map((benefit, index) => (
            <article key={benefit}>
              <span>
                <LuCheck aria-hidden />
              </span>
              <div>
                <strong>{benefit}</strong>
                <p>Use this capability to improve control, visibility, and collaboration in the workflow.</p>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section id="use-cases" className="help-doc-section">
        <h2>Common use cases</h2>
        <div className="help-doc-use-cases">
          {article.useCases.map(([title, text]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>
      {article.sections?.map(([title, text], index) => (
        <section id={`guidance-${index + 1}`} className="help-doc-section" key={title}>
          <h2>{title}</h2>
          <p>{text}</p>
        </section>
      ))}
      <Callout callout={article.callout} />
    </>
  );
}

function getToc(article) {
  if (article.type === "how-to") {
    return [
      ["overview", "Overview"],
      ...(article.prerequisites?.length ? [["before-you-start", "Before you start"]] : []),
      ["steps", "Steps"],
    ];
  }
  if (article.type === "troubleshooting") {
    return [
      ["problem", "Problem"],
      ["possible-causes", "Possible causes"],
      ["solutions", "Solutions"],
    ];
  }
  if (article.type === "faq") return [["overview", "Overview"], ["questions", "Common questions"]];
  if (article.type === "policy") {
    return [
      ["overview", "Policy overview"],
      ["requirements", "Key requirements"],
      ...(article.sections || []).map(([title], index) => [`guidance-${index + 1}`, title]),
    ];
  }
  return [
    ["overview", "Overview"],
    ["benefits", "Key benefits"],
    ["use-cases", "Common use cases"],
    ...(article.sections || []).map(([title], index) => [`guidance-${index + 1}`, title]),
  ];
}

function ArticleBody({ article }) {
  if (article.type === "how-to") return <HowToContent article={article} />;
  if (article.type === "troubleshooting") return <TroubleshootingContent article={article} />;
  if (article.type === "faq") return <FaqContent article={article} />;
  if (article.type === "policy") return <PolicyContent article={article} />;
  return <OverviewContent article={article} />;
}

export default function HelpCenterArticleDetailPage() {
  const { articleId } = useParams();
  const [loading, setLoading] = useState(true);
  const [categorySearch, setCategorySearch] = useState("");
  const [feedback, setFeedback] = useState("");
  const article = HELP_ARTICLE_BY_ID[articleId];

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "auto" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [articleId]);

  const categoryArticles = useMemo(() => {
    if (!article) return [];
    const query = categorySearch.trim().toLowerCase();
    return HELP_ARTICLES.filter(
      (item) =>
        item.category === article.category &&
        (!query || `${item.title} ${item.description}`.toLowerCase().includes(query)),
    );
  }, [article, categorySearch]);

  if (loading) {
    return (
      <div className="help-doc-page">
        <B2BHubHomeHeader />
        <main className="help-doc-loading" aria-label="Loading article">
          <div />
          <div />
          <div />
        </main>
        <HubHomeFooter />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="help-doc-page">
        <B2BHubHomeHeader />
        <main className="help-doc-error">
          <LuFileText aria-hidden />
          <h1>Article not found</h1>
          <p>The article may have moved or the link may be incorrect.</p>
          <Link to="/help-center/articles">Browse all articles</Link>
        </main>
        <HubHomeFooter />
      </div>
    );
  }

  const related = getRelatedArticles(article);
  const { previous, next } = getArticleNeighbors(article.id);
  const toc = getToc(article);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Article link copied.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  const shareArticle = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, text: article.description, url: window.location.href });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    copyLink();
  };

  const sendFeedback = (value) => {
    setFeedback(value);
    toast.success("Thank you for helping us improve this article.");
  };

  return (
    <div className="help-doc-page">
      <B2BHubHomeHeader />
      <main className="help-doc">
        <nav className="help-doc__breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>&gt;</span>
          <Link to="/help-center">Help Center</Link>
          <span aria-hidden>&gt;</span>
          <Link to={`/help-center/topics/${getArticleTopicPath(article.category)}`}>
            {HELP_CATEGORY_LABELS[article.category]}
          </Link>
          <span aria-hidden>&gt;</span>
          <span aria-current="page">{article.title}</span>
        </nav>

        <div className="help-doc__top-link">
          <Link to="/help-center/articles">
            <LuArrowLeft aria-hidden /> Back to All Articles
          </Link>
        </div>

        <div className="help-doc__layout">
          <aside className="help-doc-nav">
            <h2>{HELP_CATEGORY_LABELS[article.category]} Support</h2>
            <label>
              <input
                type="search"
                value={categorySearch}
                onChange={(event) => setCategorySearch(event.target.value)}
                placeholder="Search this category"
              />
              <LuSearch aria-hidden />
            </label>
            <nav>
              {categoryArticles.map((item) => (
                <Link
                  key={item.id}
                  to={`/help-center/articles/${item.id}`}
                  className={item.id === article.id ? "is-active" : ""}
                >
                  <LuFileText aria-hidden />
                  <span>{item.title}</span>
                </Link>
              ))}
            </nav>
            <div className="help-doc-nav__support">
              <LuMessageCircle aria-hidden />
              <div>
                <strong>Still need help?</strong>
                <p>Our support team is here to assist you.</p>
              </div>
              <Link to="/contact?enquiry=general">Contact Support</Link>
            </div>
          </aside>

          <article className={`help-doc-article help-doc-article--${article.type}`}>
            <header className="help-doc-article__header">
              <span className="help-doc-article__badge">
                {HELP_CATEGORY_LABELS[article.category]}
              </span>
              <div className="help-doc-article__title-row">
                <div>
                  <h1>{article.title}</h1>
                  <p>{article.description}</p>
                </div>
                <div className="help-doc-actions" aria-label="Article actions">
                  <button type="button" onClick={shareArticle} title="Share article">
                    <FaShareAlt aria-hidden /> <span>Share</span>
                  </button>
                  <button type="button" onClick={copyLink} title="Copy link">
                    <FaLink aria-hidden /> <span>Copy link</span>
                  </button>
                  <button type="button" onClick={() => window.print()} title="Print article">
                    <FaPrint aria-hidden /> <span>Print</span>
                  </button>
                </div>
              </div>
              <div className="help-doc-article__meta">
                <span>{article.time}</span>
                <span>Updated {article.updated}</span>
                <span>
                  <FaEye aria-hidden /> {article.views} views
                </span>
              </div>
            </header>

            <div className="help-doc-article__content">
              <ArticleBody article={article} />
            </div>

            <section className="help-doc-feedback">
              <div>
                <strong>Was this article helpful?</strong>
                <p>Your feedback helps us improve our documentation.</p>
              </div>
              <div>
                <button
                  type="button"
                  className={feedback === "yes" ? "is-selected" : ""}
                  onClick={() => sendFeedback("yes")}
                >
                  <LuThumbsUp aria-hidden /> Yes
                </button>
                <button
                  type="button"
                  className={feedback === "no" ? "is-selected is-negative" : ""}
                  onClick={() => sendFeedback("no")}
                >
                  <LuThumbsDown aria-hidden /> No
                </button>
              </div>
            </section>

            <nav className="help-doc-neighbors" aria-label="Previous and next articles">
              {previous ? (
                <Link to={`/help-center/articles/${previous.id}`}>
                  <LuArrowLeft aria-hidden />
                  <span>
                    <small>Previous article</small>
                    <strong>{previous.title}</strong>
                  </span>
                </Link>
              ) : <span />}
              {next ? (
                <Link to={`/help-center/articles/${next.id}`}>
                  <span>
                    <small>Next article</small>
                    <strong>{next.title}</strong>
                  </span>
                  <LuArrowRight aria-hidden />
                </Link>
              ) : null}
            </nav>
          </article>

          <aside className="help-doc-aside">
            {toc.length > 1 ? (
              <section className="help-doc-toc">
                <h2>On this page</h2>
                <nav>
                  {toc.map(([id, label]) => (
                    <a key={id} href={`#${id}`}>{label}</a>
                  ))}
                </nav>
              </section>
            ) : null}
            <section className="help-doc-related">
              <h2>Related Articles</h2>
              <div>
                {related.map((item) => (
                  <Link key={item.id} to={`/help-center/articles/${item.id}`}>
                    <LuFileText aria-hidden />
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.time}</small>
                    </span>
                    <LuChevronRight aria-hidden />
                  </Link>
                ))}
              </div>
              <Link to="/help-center/articles" className="help-doc-related__all">
                View All Articles <LuArrowRight aria-hidden />
              </Link>
            </section>
          </aside>
        </div>

        <section className="help-doc-cta">
          <div>
            <h2>Can't find what you're looking for?</h2>
            <p>Our support team is ready to help you.</p>
          </div>
          <div>
            <Link to="/help-center/raise-ticket" className="help-doc-cta__primary">
              <LuTicket aria-hidden /> Raise Support Ticket
            </Link>
            <Link to="/contact?enquiry=general">
              <LuMessageCircle aria-hidden /> Live Chat
            </Link>
            <a href="mailto:support@trocanet.com">
              <LuMail aria-hidden /> Email Support
            </a>
          </div>
        </section>
      </main>
      <HubHomeFooter />
    </div>
  );
}
